import { StreamLanguage, foldService } from '@codemirror/language';
import { tags } from '@lezer/highlight';

interface JsonataState {
    inBlockComment: boolean;
}

/**
 * StreamLanguage-based tokenizer for JSONata expressions.
 * Provides accurate syntax highlighting matching the official JSONata editor.
 */
export const jsonataStreamLanguage = StreamLanguage.define<JsonataState>({
    name: 'jsonata',

    tokenTable: {
        'jsonata-variable': tags.special(tags.variableName)
    },

    startState (): JsonataState {
        return { inBlockComment: false };
    },

    token (stream, state): string | null {
        // Block comment continuation from previous line
        if (state.inBlockComment) {
            const idx = stream.string.indexOf('*/', stream.pos);
            if (idx !== -1) {
                stream.pos = idx + 2;
                state.inBlockComment = false;
            } else {
                stream.skipToEnd();
            }
            return 'comment';
        }

        // Whitespace
        if (stream.eatSpace()) { return null; }

        // Block comment start
        if (stream.match('/*')) {
            const idx = stream.string.indexOf('*/', stream.pos);
            if (idx !== -1) {
                stream.pos = idx + 2;
            } else {
                state.inBlockComment = true;
                stream.skipToEnd();
            }
            return 'comment';
        }

        // Double-quoted string
        if (stream.peek() === '"') {
            stream.next();
            while (!stream.eol()) {
                const c = stream.next()!;
                if (c === '\\' && !stream.eol()) { stream.next(); } else if (c === '"') { return 'string'; }
            }
            return 'string';
        }

        // Single-quoted string
        if (stream.peek() === '\'') {
            stream.next();
            while (!stream.eol()) {
                const c = stream.next()!;
                if (c === '\\' && !stream.eol()) { stream.next(); } else if (c === '\'') { return 'string'; }
            }
            return 'string';
        }

        // Backtick-escaped identifier
        if (stream.peek() === '`') {
            stream.next();
            while (!stream.eol() && stream.next() !== '`') { /* consume */ }
            return 'variable';
        }

        // $-prefixed variable or function ($$ is the root-context variable)
        if (stream.peek() === '$') {
            stream.next();
            if (stream.peek() === '$') {
                stream.next();
                return 'jsonata-variable';
            }
            stream.eatWhile(/[a-zA-Z0-9_]/);
            return 'jsonata-variable';
        }

        // Identifier or keyword
        if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) {
            const word = stream.current();
            if (word === 'true' || word === 'false' || word === 'null' ||
                word === 'and' || word === 'or' || word === 'in') {
                return 'keyword';
            }
            return 'variable';
        }

        // Number
        if (stream.match(/^[0-9]+(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/)) {
            return 'number';
        }

        // Multi-character operators
        if (stream.match(':=') || stream.match('!=') || stream.match('<=') ||
            stream.match('>=') || stream.match('~>') || stream.match('**') ||
            stream.match('..')) {
            return 'operator';
        }

        // Single character
        const ch = stream.next()!;
        if ('.,@#;:?|=<>&!~+-*/%^'.includes(ch)) {
            return 'operator';
        }
        if ('()[]{}'.includes(ch)) {
            return 'bracket';
        }

        return null;
    },

    languageData: {
        closeBrackets: { brackets: ['(', '[', '{', '"', '\'', '`'] },
        commentTokens: { block: { open: '/*', close: '*/' } }
    }
});

/**
 * Bracket-based code folding for JSONata.
 * Folds lines ending with an opening bracket (`{`, `[`, or `(`) through to the corresponding matching closing bracket
 */
export const jsonataFoldService = foldService.of((state, lineStart, lineEnd) => {
    const lineText = state.doc.sliceString(lineStart, lineEnd);
    const match = /([{[(])\s*$/.exec(lineText);
    if (!match) { return null; }

    const openChar = match[1];
    const closeChar = openChar === '{' ? '}' : openChar === '[' ? ']' : ')';
    const openPos = lineStart + match.index;

    let depth = 1;
    let inString: string | null = null;
    let inBlockComment = false;
    let pos = openPos + 1;
    const docLen = state.doc.length;

    while (pos < docLen) {
        const line = state.doc.lineAt(pos);
        const text = state.doc.sliceString(pos, line.to);

        for (let i = 0; i < text.length; i++) {
            const c = text[i];
            const n = i + 1 < text.length ? text[i + 1] : '';

            if (inBlockComment) {
                if (c === '*' && n === '/') {
                    inBlockComment = false;
                    i++;
                }
                continue;
            }

            if (inString) {
                // Backticks escape identifiers and do not support backslash escapes.
                if (c === '\\' && inString !== '`') { i++; continue; }
                if (c === inString) { inString = null; }
                continue;
            }

            if (c === '/' && n === '*') {
                inBlockComment = true;
                i++;
                continue;
            }

            if (c === '"' || c === '\'' || c === '`') {
                inString = c;
                continue;
            }

            if (c === openChar) {
                depth++;
            } else if (c === closeChar) {
                depth--;
                if (depth === 0) {
                    return { from: openPos + 1, to: pos + i };
                }
            }
        }

        pos = line.to + 1;
    }

    return null;
});
